/*
  Emily Harrison London: product page behaviour (shop builder).

  <ehl-product> wraps the main product section. It owns:
  - variant selection from the variant JSON (price, compare-at price, savings, low stock,
    availability, sold out and unavailable option states, featured image, ?variant= URL),
  - the gallery (arrows, thumbnails, dots, swipe, zoom lightbox with keyboard support),
  - the size guide dialog (opens without touching the form, so the selection is kept),
  - accordions, the sticky purchase bar and the back to collection link,
  - add to cart with loading and error states.

  Cart contract (see content/fragments/shop.theme-liquid.md):
  1. If a listener in the capture phase already called preventDefault on the form submit,
     the shared cart integration handled it and this script does nothing.
  2. Otherwise, if window.EHL.cart.add(formData, options) exists, it is used. It must return
     a promise that rejects with an Error whose message can be shown to the shopper.
  3. Otherwise the variant is added with the Ajax Cart API (POST {root}cart/add.js) and the
     document receives a CustomEvent 'ehl:cart:added' whose detail holds the added item.
     A cart drawer that shows the result sets event.detail.handled = true; when nobody
     does, the shopper is sent to the cart page so an add is never silent.
*/
(() => {
  if (window.customElements.get('ehl-product')) return;

  const routeRoot = () => {
    const root =
      (window.EHL && window.EHL.routes && window.EHL.routes.root) ||
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const parseJSON = (node) => {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('ehl-product: invalid product JSON', error);
      return null;
    }
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class EhlProduct extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.product = parseJSON(this.querySelector('[data-product-json]'));
      if (!this.product || !Array.isArray(this.product.variants) || !this.product.variants.length) return;

      this.bound = true;
      this.cleanup = [];
      this.sectionElement = this.closest('.shopify-section') || this.parentElement;
      this.form = this.querySelector('[data-product-form]');
      this.idInput = this.querySelector('[data-variant-id-input]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.addLabel = this.querySelector('[data-add-to-cart-label]');
      this.errorNode = this.querySelector('[data-add-to-cart-error]');
      this.stickyBar = this.querySelector('[data-sticky-bar]');
      this.stickyButton = this.querySelector('[data-sticky-submit]');
      this.stickyLabel = this.querySelector('[data-sticky-label]');
      this.busy = false;

      this.labels = {
        add: this.dataset.labelAdd || 'Add to cart',
        adding: this.dataset.labelAdding || 'Adding...',
        soldOut: this.dataset.labelSoldOut || 'Sold out',
        unavailable: this.dataset.labelUnavailable || 'Unavailable',
        error: this.dataset.labelError || 'This item could not be added to your cart. Please try again.',
      };

      const initialId = Number(this.idInput ? this.idInput.value : 0);
      this.variant = this.product.variants.find((variant) => variant.id === initialId) || this.product.variants[0];
      this.selection = [...this.variant.options];

      this.bindOptions();
      this.bindGallery();
      this.bindZoom();
      this.bindSizeGuide();
      this.bindAccordions();
      this.bindStickyBar();
      this.bindForm();
      this.bindShippingEstimate();
      this.revealBackLink();

      this.render({ updateUrl: false, scrollMedia: false });
      this.showMedia(this.variant.featured_media_id, 'auto');
    }

    disconnectedCallback() {
      (this.cleanup || []).forEach((fn) => fn());
      this.cleanup = [];
      this.bound = false;
      document.body.classList.remove('has-product-sticky-bar');
    }

    on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanup.push(() => target.removeEventListener(type, handler, options));
    }

    /* Variants */

    bindOptions() {
      this.optionButtons = [...this.querySelectorAll('[data-option-button]')];
      this.optionButtons.forEach((button) => {
        this.on(button, 'click', () => {
          if (button.disabled) return;
          this.selectOption(Number(button.dataset.optionIndex), button.dataset.optionValue);
        });
      });
    }

    exactVariant(selection) {
      return this.product.variants.find((variant) =>
        variant.options.every((value, index) => value === selection[index])
      );
    }

    bestVariant(selection, changedIndex) {
      const candidates = this.product.variants.filter(
        (variant) => variant.options[changedIndex] === selection[changedIndex]
      );
      if (!candidates.length) return null;
      const score = (variant) =>
        variant.options.reduce((total, value, index) => total + (value === selection[index] ? 1 : 0), 0);
      return candidates.sort((a, b) => {
        if (a.available !== b.available) return a.available ? -1 : 1;
        return score(b) - score(a);
      })[0];
    }

    selectOption(index, value) {
      const selection = [...this.selection];
      selection[index] = value;
      const variant = this.exactVariant(selection) || this.bestVariant(selection, index);
      if (!variant) return;
      this.variant = variant;
      this.selection = [...variant.options];
      this.hideError();
      this.render({ updateUrl: true, scrollMedia: true });
    }

    optionState(index, value) {
      const prefix = this.selection.slice(0, index);
      const matches = this.product.variants.filter(
        (variant) =>
          variant.options[index] === value && prefix.every((selected, position) => variant.options[position] === selected)
      );
      if (!matches.length) return 'unavailable';
      return matches.some((variant) => variant.available) ? 'available' : 'sold-out';
    }

    render({ updateUrl, scrollMedia }) {
      const variant = this.variant;

      if (this.idInput) this.idInput.value = variant.id;

      this.optionButtons.forEach((button) => {
        const index = Number(button.dataset.optionIndex);
        const value = button.dataset.optionValue;
        const isActive = this.selection[index] === value;
        const state = this.optionState(index, value);
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        button.classList.toggle('is-unavailable', state !== 'available');
        button.disabled = state === 'unavailable' && !isActive;
        button.dataset.state = state;
        const status = button.querySelector('[data-option-status]');
        const statusText =
          state === 'sold-out' ? this.labels.soldOut : state === 'unavailable' ? this.labels.unavailable : '';
        if (status) status.textContent = statusText ? `, ${statusText}` : '';
        // Colour swatches are named by aria-label, which hides the status text inside them,
        // so the status is added to the label too.
        if (button.hasAttribute('aria-label')) {
          if (!button.dataset.baseLabel) button.dataset.baseLabel = button.getAttribute('aria-label');
          button.setAttribute(
            'aria-label',
            statusText ? `${button.dataset.baseLabel}, ${statusText}` : button.dataset.baseLabel
          );
        }
        if (button.classList.contains('product-info__option-button--normal')) {
          if (statusText) button.title = statusText;
          else button.removeAttribute('title');
        }
      });

      this.querySelectorAll('[data-option-group]').forEach((group) => {
        const label = group.querySelector('[data-selected-value]');
        const index = Number(group.dataset.optionIndex);
        if (label) label.textContent = this.selection[index] || '';
      });

      this.renderPrice(variant);
      this.renderAvailability(variant);

      const lowStock = this.querySelector('[data-low-stock]');
      if (lowStock) lowStock.hidden = !variant.low_stock;

      if (scrollMedia) this.showMedia(variant.featured_media_id);

      if (updateUrl && this.dataset.productUrl) {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        const current = new URL(window.location.href);
        current.searchParams.forEach((paramValue, key) => {
          if (key !== 'variant') url.searchParams.set(key, paramValue);
        });
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({ variantId: variant.id }, '', `${url.pathname}${url.search}${url.hash}`);
      }

      this.dispatchEvent(
        new CustomEvent('ehl:variant:change', {
          bubbles: true,
          detail: { sectionId: this.dataset.sectionId, variant },
        })
      );
    }

    renderPrice(variant) {
      const price = this.querySelector('[data-product-price]');
      const compareWrap = this.querySelector('[data-product-compare-wrap]');
      const compare = this.querySelector('[data-product-compare-price]');
      const savings = this.querySelector('[data-product-savings-badge]');
      const priceLabel = this.querySelector('[data-price-label]');
      const stickyPrice = this.querySelector('[data-sticky-price]');

      if (price) price.textContent = variant.price_formatted;
      if (stickyPrice) stickyPrice.textContent = variant.price_formatted;
      if (compareWrap) compareWrap.hidden = !variant.on_sale;
      if (compare) compare.textContent = variant.on_sale ? variant.compare_formatted : '';
      if (priceLabel && compareWrap) {
        const labels = compareWrap.closest('.product-info__price-wrap');
        priceLabel.textContent = variant.on_sale
          ? (labels && labels.dataset.labelSale) || priceLabel.textContent
          : (labels && labels.dataset.labelRegular) || priceLabel.textContent;
      }
      if (savings) {
        if (variant.on_sale) {
          const template = this.dataset.savingsTemplate || 'Save [[value]]';
          const value = this.dataset.savingsFormat === 'amount' ? variant.saving_amount : `${variant.saving_percent}%`;
          savings.textContent = template.replace('[[value]]', value);
          savings.hidden = false;
        } else {
          savings.textContent = '';
          savings.hidden = true;
        }
      }
    }

    renderAvailability(variant) {
      const label = variant.available ? this.labels.add : this.labels.soldOut;
      if (this.addButton) this.addButton.disabled = this.busy || !variant.available;
      if (this.addLabel && !this.busy) this.addLabel.textContent = label;
      if (this.stickyButton) this.stickyButton.disabled = this.busy || !variant.available;
      if (this.stickyLabel && !this.busy) this.stickyLabel.textContent = label;
    }

    /* Gallery */

    bindGallery() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      if (!this.viewport) return;
      this.slides = [...this.viewport.querySelectorAll('[data-media-id]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
      this.thumbStrip = this.querySelector('[data-gallery-thumbs]');

      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) this.on(prev, 'click', () => this.stepMedia(-1));
      if (next) this.on(next, 'click', () => this.stepMedia(1));

      [...this.thumbs, ...this.dots].forEach((button) => {
        this.on(button, 'click', () => this.showMedia(Number(button.dataset.targetMediaId)));
      });

      if ('IntersectionObserver' in window && this.slides.length > 1) {
        const observer = new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter((entry) => entry.isIntersecting)
              .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
            if (visible) this.setActiveMedia(Number(visible.target.dataset.mediaId));
          },
          { root: this.viewport, threshold: 0.55 }
        );
        this.slides.forEach((slide) => observer.observe(slide));
        this.cleanup.push(() => observer.disconnect());
      }
    }

    activeIndex() {
      const index = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      return index < 0 ? 0 : index;
    }

    stepMedia(direction) {
      if (!this.slides || !this.slides.length) return;
      const count = this.slides.length;
      const nextIndex = (this.activeIndex() + direction + count) % count;
      this.showMedia(Number(this.slides[nextIndex].dataset.mediaId));
    }

    showMedia(mediaId, behavior) {
      if (!mediaId || !this.viewport || !this.slides) return;
      const slide = this.slides.find((item) => Number(item.dataset.mediaId) === Number(mediaId));
      if (!slide) return;
      this.viewport.scrollTo({
        left: slide.offsetLeft,
        behavior: behavior || (prefersReducedMotion() ? 'auto' : 'smooth'),
      });
      this.setActiveMedia(Number(mediaId));
    }

    setActiveMedia(mediaId) {
      if (!this.slides) return;
      this.slides.forEach((slide) => slide.classList.toggle('is-active', Number(slide.dataset.mediaId) === mediaId));
      [...this.thumbs, ...this.dots].forEach((button) => {
        const isActive = Number(button.dataset.targetMediaId) === mediaId;
        button.classList.toggle('is-active', isActive);
        if (isActive) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      const activeThumb = this.thumbs.find((button) => button.classList.contains('is-active'));
      if (activeThumb && this.thumbStrip && this.thumbStrip.scrollWidth > this.thumbStrip.clientWidth) {
        const left = activeThumb.offsetLeft - (this.thumbStrip.clientWidth - activeThumb.offsetWidth) / 2;
        this.thumbStrip.scrollTo({ left: Math.max(0, left), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }

    /* Zoom lightbox */

    bindZoom() {
      this.modal = this.querySelector('[data-gallery-modal]');
      if (!this.modal) return;
      this.modalStage = this.modal.querySelector('[data-gallery-modal-stage]');
      this.modalThumbs = [...this.modal.querySelectorAll('[data-gallery-modal-thumb]')];
      this.zoomSlides = (this.slides || []).filter((slide) => slide.dataset.zoomSrc);
      const close = this.modal.querySelector('[data-gallery-close]');

      this.querySelectorAll('[data-gallery-zoom]').forEach((button) => {
        this.on(button, 'click', () => {
          const slide = button.closest('[data-media-id]');
          if (!slide) return;
          this.zoomTrigger = button;
          this.renderZoom(Number(slide.dataset.mediaId));
          document.documentElement.classList.add('product-modal-open');
          this.modal.showModal();
          if (close) close.focus();
        });
      });

      this.modalThumbs.forEach((thumb) => {
        this.on(thumb, 'click', () => this.renderZoom(Number(thumb.dataset.targetMediaId)));
      });

      if (close) this.on(close, 'click', () => this.modal.close());

      this.on(this.modal, 'click', (event) => {
        if (event.target === this.modal || event.target === this.modalStage) this.modal.close();
      });

      this.on(this.modal, 'keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.stepZoom(event.key === 'ArrowLeft' ? -1 : 1);
        }
      });

      this.on(this.modal, 'close', () => {
        if (this.modalStage) this.modalStage.replaceChildren();
        document.documentElement.classList.remove('product-modal-open');
        if (this.zoomMediaId) this.showMedia(this.zoomMediaId, 'auto');
        this.zoomMediaId = null;
        const activeSlide = (this.slides || []).find((item) => item.classList.contains('is-active'));
        const focusTarget = (activeSlide && activeSlide.querySelector('[data-gallery-zoom]')) || this.zoomTrigger;
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      });
    }

    renderZoom(mediaId) {
      const slide = this.zoomSlides.find((item) => Number(item.dataset.mediaId) === mediaId);
      if (!slide || !this.modalStage) return;
      const image = document.createElement('img');
      image.src = slide.dataset.zoomSrc;
      image.alt = slide.dataset.zoomAlt || '';
      if (slide.dataset.zoomWidth) image.width = Number(slide.dataset.zoomWidth);
      if (slide.dataset.zoomHeight) image.height = Number(slide.dataset.zoomHeight);
      image.decoding = 'async';
      this.modalStage.replaceChildren(image);
      this.zoomMediaId = mediaId;
      this.modalThumbs.forEach((thumb) => {
        const isActive = Number(thumb.dataset.targetMediaId) === mediaId;
        thumb.classList.toggle('is-active', isActive);
        if (isActive) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    stepZoom(direction) {
      if (!this.zoomSlides.length || !this.zoomMediaId) return;
      const ids = this.zoomSlides.map((slide) => Number(slide.dataset.mediaId));
      const index = ids.indexOf(this.zoomMediaId);
      const nextIndex = (index + direction + ids.length) % ids.length;
      this.renderZoom(ids[nextIndex]);
    }

    /* Size guide */

    bindSizeGuide() {
      const dialog = this.querySelector('[data-size-guide]');
      if (!dialog) return;
      let opener = null;
      this.querySelectorAll('[data-size-guide-open]').forEach((button) => {
        this.on(button, 'click', () => {
          opener = button;
          document.documentElement.classList.add('product-modal-open');
          dialog.showModal();
          const close = dialog.querySelector('[data-size-guide-close]');
          if (close) close.focus();
        });
      });
      dialog.querySelectorAll('[data-size-guide-close]').forEach((button) => {
        this.on(button, 'click', () => dialog.close());
      });
      this.on(dialog, 'click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      this.on(dialog, 'close', () => {
        document.documentElement.classList.remove('product-modal-open');
        if (opener) opener.focus({ preventScroll: true });
      });
    }

    /* Accordions */

    bindAccordions() {
      this.querySelectorAll('[data-accordion-trigger]').forEach((trigger) => {
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;
        this.on(trigger, 'click', () => {
          const open = trigger.getAttribute('aria-expanded') !== 'true';
          trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
          this.animatePanel(panel, open);
        });
      });
    }

    animatePanel(panel, open) {
      const finish = () => {
        panel.style.height = '';
        if (!open) panel.hidden = true;
        panel.classList.toggle('is-open', open);
      };
      if (prefersReducedMotion()) {
        panel.hidden = !open;
        finish();
        return;
      }
      if (open) {
        panel.hidden = false;
        panel.style.height = '0px';
        const target = panel.scrollHeight;
        window.requestAnimationFrame(() => {
          panel.style.height = `${target}px`;
        });
      } else {
        panel.style.height = `${panel.scrollHeight}px`;
        window.requestAnimationFrame(() => {
          panel.style.height = '0px';
        });
      }
      const onEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'height') return;
        panel.removeEventListener('transitionend', onEnd);
        finish();
      };
      panel.addEventListener('transitionend', onEnd);
    }

    /* Sticky purchase bar */

    bindStickyBar() {
      if (!this.stickyBar || !this.addButton) return;
      if (this.stickyButton) {
        this.on(this.stickyButton, 'click', () => {
          if (this.stickyButton.disabled) return;
          this.lastTrigger = this.stickyButton;
          if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.addButton);
          else this.addButton.click();
        });
      }
      let seenButton = false;
      let frame = null;
      const update = () => {
        frame = null;
        const rect = this.addButton.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        if (rect.bottom > 0 && rect.top < viewportHeight) seenButton = true;
        const show = seenButton && rect.bottom <= 0;
        if (this.stickyBar.hidden === show) {
          this.stickyBar.hidden = !show;
          document.body.classList.toggle('has-product-sticky-bar', show);
          if (show) document.body.style.setProperty('--product-sticky-bar-height', `${this.stickyBar.offsetHeight}px`);
        }
      };
      const request = () => {
        if (frame === null) frame = window.requestAnimationFrame(update);
      };
      update();
      this.on(window, 'scroll', request, { passive: true });
      this.on(window, 'resize', request);
    }

    /* Add to cart */

    bindForm() {
      if (!this.form) return;
      this.on(this.form, 'submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      if (event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.busy) return;

      const variant = this.variant;
      if (!variant || !variant.available) {
        this.showError(variant ? this.labels.soldOut : this.labels.unavailable);
        return;
      }

      const trigger = this.lastTrigger || this.addButton;
      this.lastTrigger = null;
      this.hideError();

      const formData = new FormData(this.form);
      formData.set('id', String(variant.id));
      if (!formData.get('quantity')) formData.set('quantity', '1');

      this.setBusy(true);
      try {
        const shared = window.EHL && window.EHL.cart;
        if (shared && typeof shared.add === 'function') {
          await shared.add(formData, { trigger, source: 'product-form', variant });
        } else {
          const response = await fetch(`${routeRoot()}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          const data = await response.json().catch(() => null);
          if (!response.ok || !data || data.status) {
            throw new Error((data && (data.description || data.message)) || this.labels.error);
          }
          const detail = { source: 'product-form', item: data, variant, trigger, handled: false };
          document.dispatchEvent(new CustomEvent('ehl:cart:added', { detail }));
          if (!detail.handled) {
            window.location.href = this.dataset.cartUrl || `${routeRoot()}cart`;
            return;
          }
        }
      } catch (error) {
        this.showError(error && error.message ? error.message : this.labels.error);
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.busy = busy;
      if (this.addButton) {
        this.addButton.setAttribute('aria-busy', busy ? 'true' : 'false');
        this.addButton.disabled = busy || !this.variant.available;
      }
      if (this.stickyButton) this.stickyButton.disabled = busy || !this.variant.available;
      const label = busy ? this.labels.adding : this.variant.available ? this.labels.add : this.labels.soldOut;
      if (this.addLabel) this.addLabel.textContent = label;
      if (this.stickyLabel) this.stickyLabel.textContent = label;
    }

    showError(message) {
      if (!this.errorNode) return;
      this.errorNode.textContent = message || this.labels.error;
      this.errorNode.hidden = false;
    }

    hideError() {
      if (!this.errorNode) return;
      this.errorNode.hidden = true;
      this.errorNode.textContent = '';
    }

    /* Shipping estimate (configured working days only) */

    bindShippingEstimate() {
      const node = this.querySelector('[data-shipping-estimate]');
      const target = node && node.querySelector('[data-shipping-date]');
      if (!node || !target) return;
      const minDays = Number(node.dataset.minDays || 0);
      const maxDays = Number(node.dataset.maxDays || 0);
      if (maxDays <= 0) return;
      const addWorkingDays = (days) => {
        const date = new Date();
        let added = 0;
        while (added < days) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (day !== 0 && day !== 6) added += 1;
        }
        return date;
      };
      const locale = node.dataset.locale || document.documentElement.lang || 'en-GB';
      const format = (date) => date.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
      const first = addWorkingDays(Math.min(minDays, maxDays));
      const last = addWorkingDays(maxDays);
      target.textContent =
        minDays > 0 && minDays < maxDays ? `${format(first)} and ${format(last)}` : format(last);
      if (minDays > 0 && minDays < maxDays) target.textContent = `between ${target.textContent}`;
    }

    /* Back to collection */

    revealBackLink() {
      const scope = this.sectionElement || document;
      const nav = scope.querySelector('[data-back-to-collection]');
      if (!nav) return;
      try {
        const url = window.sessionStorage.getItem('ehl_last_collection_url');
        const title = window.sessionStorage.getItem('ehl_last_collection_title');
        if (!url) return;
        const parsed = new URL(url, window.location.origin);
        if (parsed.origin !== window.location.origin) return;
        // Only when the shopper arrived from that collection (the reference shows the last
        // collection of the session even for products opened from the homepage or search).
        const referrer = document.referrer ? new URL(document.referrer) : null;
        if (!referrer || referrer.origin !== window.location.origin || referrer.pathname !== parsed.pathname) return;
        const link = nav.querySelector('[data-back-to-collection-link]');
        const label = nav.querySelector('[data-back-to-collection-label]');
        if (link) link.href = `${parsed.pathname}${parsed.search}`;
        if (label && title) label.textContent = title;
        nav.hidden = false;
      } catch (error) {
        /* Storage can be blocked; the link simply stays hidden. */
      }
    }
  }

  window.customElements.define('ehl-product', EhlProduct);

  /* Mobile swipe row with a progress track (recommendations under the product). */
  if (!window.customElements.get('ehl-scroll-progress')) {
    class EhlScrollProgress extends HTMLElement {
      connectedCallback() {
        this.scroller = this.querySelector('[data-scroll-progress-scroller]');
        this.track = this.querySelector('[data-scroll-progress-track]');
        this.fill = this.querySelector('[data-scroll-progress-fill]');
        if (!this.scroller || !this.fill) return;
        this.update = this.update.bind(this);
        this.scroller.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update);
        this.update();
      }

      disconnectedCallback() {
        if (!this.scroller) return;
        this.scroller.removeEventListener('scroll', this.update);
        window.removeEventListener('resize', this.update);
      }

      update() {
        const { scrollLeft, clientWidth, scrollWidth } = this.scroller;
        const scrollable = scrollWidth > clientWidth + 1;
        if (this.track) this.track.hidden = !scrollable;
        const progress = scrollable ? Math.min(1, (scrollLeft + clientWidth) / scrollWidth) : 1;
        this.fill.style.width = `${(progress * 100).toFixed(2)}%`;
      }
    }
    window.customElements.define('ehl-scroll-progress', EhlScrollProgress);
  }

  /* Product recommendations: related products fetched with the Section Rendering API. */
  if (!window.customElements.get('ehl-product-recommendations')) {
    class EhlProductRecommendations extends HTMLElement {
      connectedCallback() {
        const url = this.dataset.url;
        if (!url || this.dataset.loaded === 'true') return;
        const load = () => {
          this.dataset.loaded = 'true';
          fetch(url)
            .then((response) => (response.ok ? response.text() : ''))
            .then((text) => {
              if (!text) return;
              const html = new DOMParser().parseFromString(text, 'text/html');
              const fresh = html.querySelector(`ehl-product-recommendations[data-section-id="${this.dataset.sectionId}"]`);
              if (fresh && fresh.innerHTML.trim().length) {
                this.innerHTML = fresh.innerHTML;
                this.classList.add('is-loaded');
                this.dispatchEvent(new CustomEvent('ehl:recommendations:loaded', { bubbles: true }));
              } else {
                const section = this.closest('.shopify-section');
                if (section) section.hidden = true;
              }
            })
            .catch(() => {
              const section = this.closest('.shopify-section');
              if (section) section.hidden = true;
            });
        };
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) {
                observer.disconnect();
                load();
              }
            },
            { rootMargin: '0px 0px 400px 0px' }
          );
          observer.observe(this);
        } else {
          load();
        }
      }
    }
    window.customElements.define('ehl-product-recommendations', EhlProductRecommendations);
  }
})();
